import {
  type MenuItem,
  type DropdownMenuItem,
  type SimpleMenuItem,
  type PopoverMenuItem,
} from '@/features/sidebar/types'
import { Link, NavLink } from 'react-router-dom'
import { Accordion } from '@base-ui-components/react/accordion'
import { Icon } from '@/ui/icon2'
import { NoveltyIndicator } from '@/features/sidebar/components/NoveltyIndicator'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem as PopoverDropdownMenuItem,
} from '@/ui/Popover'
import rootStore from '@/app/store'
import style from './index.module.css'
import { Dispatch, SetStateAction, useState, useEffect } from 'react'

export const Item = ({
  isCollapsed,
  setIsCollapsed,
  item,
  dataScreenshotsId,
}: {
  isCollapsed?: boolean
  setIsCollapsed?: Dispatch<SetStateAction<boolean>>
  item: MenuItem
  dataScreenshotsId: string
}) => {
  return (
    <>
      {'submenu' in item ? (
        <DropdownItem
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          item={item as DropdownMenuItem}
          dataScreenshotsId={dataScreenshotsId}
        />
      ) : 'popover' in item ? (
        <PopoverItem
          isCollapsed={isCollapsed}
          item={item as PopoverMenuItem}
          dataScreenshotsId={dataScreenshotsId}
        />
      ) : (
        <SimpleItem
          isCollapsed={isCollapsed}
          item={item as SimpleMenuItem}
          dataScreenshotsId={dataScreenshotsId}
        />
      )}
    </>
  )
}

const SimpleItem = ({
  isCollapsed,
  item,
  dataScreenshotsId,
}: {
  isCollapsed?: boolean
  item: SimpleMenuItem
  dataScreenshotsId: string
}) => {
  const handleClick = () => {
    if (item.targetKey) {
      rootStore.noveltyFeedback.markAsSeen(item.targetKey)
    }
  }

  return (
    <NavLink
      to={item.to || ''}
      className={style.item}
      onClick={handleClick}
      data-screenshots-id={dataScreenshotsId}
    >
      <Icon name={item.icon} />
      {!isCollapsed && (
        <div className={style.label}>
          {item.label}

          {item.targetKey && (
            <NoveltyIndicator targetKey={item.targetKey} position="top-right" />
          )}
        </div>
      )}
    </NavLink>
  )
}

const DropdownItem = ({
  isCollapsed,
  setIsCollapsed,
  item,
  dataScreenshotsId,
}: {
  isCollapsed?: boolean
  setIsCollapsed?: Dispatch<SetStateAction<boolean>>
  item: DropdownMenuItem
  dataScreenshotsId: string
}) => {
  const [accordionValue, setAccordionValue] = useState<any[]>(
    item.openByDefault ? [item.label] : [],
  )

  const handleChange = (value: any[]) => {
    setAccordionValue(value)
    if (value.length !== 0 && setIsCollapsed) {
      setIsCollapsed(false)
    }
  }

  // Cerrar el accordion cuando la sidebar se expanda
  useEffect(() => {
    if (isCollapsed === true) {
      setAccordionValue([])
    }
  }, [isCollapsed])

  return (
    <>
      <Accordion.Root value={accordionValue} onValueChange={handleChange}>
        <Accordion.Item
          value={item.label}
          className={style.dropdown}
          data-screenshots-id={dataScreenshotsId}
        >
          <Accordion.Trigger className={style.item}>
            <Icon name={item.icon} />
            {!isCollapsed && (
              <>
                <div className={style.label}>{item.label}</div>
                <div className={style.triggerIcon}>
                  <Icon size={16} name="chevron-down" />
                </div>
              </>
            )}
          </Accordion.Trigger>
          <Accordion.Panel className={style.panel}>
            <div className={style.content}>
              {item.submenuTitle && (
                <div className={style.submenuTitle}>{item.submenuTitle}</div>
              )}
              <ul className={style.submenu}>
                {item.submenu.map((submenuItem) => (
                  <li key={submenuItem.to}>
                    <NavLink to={submenuItem.to || ''} className={style.item}>
                      <div className={style.label}>{submenuItem.label}</div>
                    </NavLink>
                  </li>
                ))}
              </ul>
              {item.to && (
                <Link to={item.to} className={style.viewMore}>
                  <div>Ver más</div>
                  <Icon size={14} name="chevron-right" />
                </Link>
              )}
            </div>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion.Root>
    </>
  )
}

const PopoverItem = ({
  isCollapsed,
  item,
  dataScreenshotsId,
}: {
  isCollapsed?: boolean
  item: PopoverMenuItem
  dataScreenshotsId: string
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={style.item}
        data-screenshots-id={dataScreenshotsId}
      >
        <Icon name={item.icon} />
        {!isCollapsed && (
          <>
            <div className={style.label}>{item.label}</div>
            <div className={style.triggerIcon}>
              <Icon size={16} name="chevron-down" />
            </div>
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent sideOffset={8}>
        {item.popover.map((popoverItem) => (
          <PopoverDropdownMenuItem
            key={popoverItem.to}
            to={popoverItem.to}
            dataScreenshotsId={popoverItem.dataScreenshotsId}
          >
            <span>{popoverItem.label}</span>
          </PopoverDropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
